import { useContext, useEffect, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

import {
  stateEntityAdapter,
  selectEnergyStates,
} from '@racepointenergy/swc/scs/features/host-states'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import type { HomeId } from '@racepointenergy/swc/scs/types'

import theme from 'theme_'
import { HomeStateContext } from 'features/home/homeStateContext'
import { H1 } from 'components/Typography/Headings'
import PowerCard from './PowerCard'
import {
  BatteryIcon,
  GeneratorIcon,
  GridErrorIcon,
  GridIcon,
  HomeIcon,
  SolarIcon,
} from './Icons'
import { messages } from '../messages'
import { PowerCardsWrapper } from './styles'
import { formatPower } from '../utils'
import AvailabilityChecker from 'features/home/SysMonGuard/AvailabilityChecker'
import { notAvailableHostMessages } from '../../HomeStatus/MainInfo/messages'
import NotAvailableHost from '../../HomeStatus/NotAvailableHost'

const hostStateInitialState = stateEntityAdapter.getInitialState()

interface PowerFlowProps {
  homeId: HomeId
}

function PowerFlow({ homeId }: PowerFlowProps) {
  const { formatMessage } = useIntl()
  const { isOnline } = useContext(HomeStateContext)
  const [batteryStatus, setBatteryStatus] = useState({
    status: 'inactive',
    statusMessage: '',
    icon: <></>,
  })
  const [solarStatus, setSolarStatus] = useState({
    status: 'inactive',
    statusMessage: '',
    icon: <></>,
  })
  const [generatorStatus, setGeneratorStatus] = useState({
    status: 'inactive',
    statusMessage: '',
    icon: <></>,
  })
  const [gridStatus, setGridStatus] = useState({
    status: 'inactive',
    statusMessage: '',
    icon: <></>,
  })

  const {
    data = hostStateInitialState,
    isError,
    isFetching,
    error,
  } = dealerMonitorStates.useQuery({ id: homeId })
  const states = selectEnergyStates(data)

  useEffect(() => {
    if (isError) {
      console.log('Error getting system states: ', error)
    }
  }, [isError, error])

  useEffect(() => {
    const batteryModeNumber = Number(states.batteryMode)
    switch (batteryModeNumber) {
      case 0:
        setBatteryStatus({
          status: 'inactive',
          statusMessage: formatMessage(messages.inactive),
          icon: <BatteryIcon inactive charge={states.stateOfCharge} />,
        })
        break
      case 1:
        setBatteryStatus({
          status: 'warning',
          statusMessage: formatMessage(messages.charging),
          icon: (
            <BatteryIcon
              charge={states.stateOfCharge}
              color={'rgb(51, 113, 255)'}
            />
          ),
        })
        break
      case 2:
        setBatteryStatus({
          status: 'active',
          statusMessage: formatMessage(messages.discharging),
          icon: (
            <BatteryIcon
              charge={states.stateOfCharge}
              color={theme.colors.successGreen}
            />
          ),
        })
        break
      default:
        setBatteryStatus({
          status: 'inactive',
          statusMessage: formatMessage(messages.inactive),
          icon: <BatteryIcon inactive charge={100} />,
        })
        break
    }

    setSolarStatus({
      status: states.isSolarProducing ? 'active' : 'inactive',
      statusMessage: formatMessage(
        states.isSolarProducing ? messages.producing : messages.inactive
      ),
      icon: (
        <SolarIcon
          color={
            states.isSolarProducing
              ? theme.colors.successGreen
              : theme.colors.beachGrey
          }
        />
      ),
    })

    if (!states.isGridAvailable) {
      setGridStatus({
        status: 'error',
        statusMessage: formatMessage(messages.error),
        icon: <GridErrorIcon />,
      })
    } else if (states.gridPower === 0) {
      setGridStatus({
        status: 'inactive',
        statusMessage: formatMessage(messages.inactive),
        icon: <GridIcon />,
      })
    } else if (states.gridPower < 0) {
      setGridStatus({
        status: 'active',
        statusMessage: formatMessage(messages.exporting),
        icon: <GridIcon exporting color={theme.colors.duneWarmGrey} />,
      })
    } else {
      setGridStatus({
        status: 'active',
        statusMessage: formatMessage(messages.importing),
        icon: <GridIcon color={theme.colors.successGreen} />,
      })
    }

    if (states.generatorStatus === 3 || states.generatorStatus === 4) {
      setGeneratorStatus({
        status: 'error',
        statusMessage: formatMessage(messages.error),
        icon: <GeneratorIcon error />,
      })
    } else {
      setGeneratorStatus({
        status: states.generatorRunning ? 'active' : 'inactive',
        statusMessage: formatMessage(
          states.generatorRunning ? messages.producing : messages.inactive
        ),
        icon: (
          <GeneratorIcon
            color={
              states.generatorPower !== 0
                ? theme.colors.successGreen
                : theme.colors.beachGrey
            }
          />
        ),
      })
    }
  }, [
    states.batteryMode,
    states.stateOfCharge,
    states.isSolarProducing,
    states.isGridAvailable,
    states.gridPower,
    states.generatorStatus,
    states.generatorRunning,
    states.generatorPower,
    formatMessage,
  ])

  if (data.entities[`Energy.FeatureLevel`]?.value === 'None') {
    return (
      <>
        <H1>
          <FormattedMessage {...messages.powerFlowHeader} />
        </H1>
        <NotAvailableHost
          title={formatMessage(messages.energyUnavailable)}
          captions={[]}
        />
      </>
    )
  }

  if (!isOnline) {
    return (
      <AvailabilityChecker homeId={homeId}>
        {({ errors }) => {
          return (
            <>
              <H1>
                <FormattedMessage {...messages.powerFlowHeader} />
              </H1>
              <NotAvailableHost
                title={formatMessage(
                  notAvailableHostMessages.sysMonUnavailableTitle
                )}
                captions={errors}
              />
            </>
          )
        }}
      </AvailabilityChecker>
    )
  }

  return (
    <div>
      <H1>
        <FormattedMessage {...messages.powerFlowHeader} />
      </H1>
      <PowerCardsWrapper>
        <PowerCard
          loading={isFetching}
          status="active"
          icon={<HomeIcon />}
          title={formatMessage(messages.houseHeader, { br: <br /> })}
          text={formatPower(states.powerConsumption, { absolute: true })}
        />
        <PowerCard
          loading={isFetching}
          status={gridStatus.status}
          icon={gridStatus.icon}
          title={formatMessage(messages.gridHeader, {
            br: <br />,
            status: gridStatus.statusMessage,
          })}
          text={formatPower(states.netPower, { absolute: true })}
        />

        {states.hasSolar && (
          <PowerCard
            loading={isFetching}
            status={solarStatus.status}
            icon={solarStatus.icon}
            title={formatMessage(messages.solarHeader, {
              br: <br />,
              status: solarStatus.statusMessage,
            })}
            text={formatPower(states.solarPower, { absolute: true })}
          />
        )}

        {states.hasBattery && (
          <PowerCard
            loading={isFetching}
            status={batteryStatus.status}
            icon={batteryStatus.icon}
            title={formatMessage(messages.batteryHeader, {
              br: <br />,
              status: batteryStatus.statusMessage,
            })}
            text={formatPower(states.batteryPower, { absolute: true })}
            charge={`${states.stateOfCharge}%`}
          />
        )}

        {states.hasGenerator && (
          <PowerCard
            loading={isFetching}
            status={generatorStatus.status}
            icon={generatorStatus.icon}
            title={formatMessage(messages.generatorHeader, {
              br: <br />,
              status: generatorStatus.statusMessage,
            })}
            text={formatPower(states.generatorPower, {
              absolute: true,
            })}
          />
        )}
      </PowerCardsWrapper>
    </div>
  )
}

export default PowerFlow
